// "How did you find Ultimo Bots?" — asked once per account, as a small card that floats over the bottom of
// the page (no layout shift). One click answers; the AI and "Other" answers ask one short follow-up. Closing it
// counts as "don't ask again". Only recorded for "where customers come from" (backend src/controller/acquisition.py).
// Never blocks anything: every request failure just hides the card.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authFetch } from "../utils/api";
import "./SourceQuestion.css";

const API = process.env.REACT_APP_API_BASE_URL;
const OPTIONS = [
  "wix_app_market", "google", "ai_assistant", "blog_article", "recommendation", "agency", "youtube",
  "social_media", "saw_on_website", "ad", "shopify_app_store", "webflow_marketplace", "wordpress_directory", "other",
];
const AI_NAMES = ["ChatGPT", "Gemini", "Claude", "Perplexity", "Copilot"];
// Not while an agent is being created or an onboarding runs.
const QUIET_PATHS = ["/agents/new", "/wix", "/wordpress-register", "/webflow-register", "/create-chatbot", "/mcp"];

export default function SourceQuestion() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [ask, setAsk] = useState(false);
  const [step, setStep] = useState("pick"); // pick | ai | other | thanks
  const [detail, setDetail] = useState("");
  const quiet = QUIET_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const r = await authFetch(`${API}acquisition/question`);
        if (!r || !r.ok) return;
        const j = await r.json();
        if (alive && j.ask) setAsk(true);
      } catch (e) {
        // no card
      }
    }, 5000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  if (!ask || quiet) return null;

  const send = async (answer, answerDetail) => {
    setStep("thanks");
    try {
      await authFetch(`${API}acquisition/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer, detail: answerDetail || null }),
      });
    } catch (e) {
      // the card closes anyway
    }
    setTimeout(() => setAsk(false), 1600);
  };

  const close = async () => {
    setAsk(false);
    try {
      await authFetch(`${API}acquisition/dismiss`, { method: "POST" });
    } catch (e) {
      // closed anyway
    }
  };

  const pick = (key) => {
    if (key === "ai_assistant") setStep("ai");
    else if (key === "other") setStep("other");
    else send(key);
  };

  return (
    <div className="srcq" role="dialog" aria-label={t("srcq_title")}>
      <button type="button" className="srcq-close" onClick={close} aria-label={t("srcq_close")}>
        ×
      </button>
      {step === "thanks" ? (
        <p className="srcq-thanks">{t("srcq_thanks")}</p>
      ) : (
        <>
          <p className="srcq-title">{step === "ai" ? t("srcq_which_ai") : t("srcq_title")}</p>
          {step === "pick" && (
            <div className="srcq-chips">
              {OPTIONS.map((key) => (
                <button type="button" key={key} className="srcq-chip" onClick={() => pick(key)}>
                  {t(`srcq_opt_${key}`)}
                </button>
              ))}
            </div>
          )}
          {step === "ai" && (
            <div className="srcq-chips">
              {AI_NAMES.map((name) => (
                <button type="button" key={name} className="srcq-chip" onClick={() => send("ai_assistant", name)}>
                  {name}
                </button>
              ))}
              <button type="button" className="srcq-chip" onClick={() => send("ai_assistant", "other")}>
                {t("srcq_opt_other")}
              </button>
            </div>
          )}
          {step === "other" && (
            <form
              className="srcq-other"
              onSubmit={(e) => {
                e.preventDefault();
                send("other", detail.trim());
              }}
            >
              <input
                autoFocus
                maxLength={200}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder={t("srcq_other_placeholder")}
              />
              <button type="submit" className="srcq-send">
                {t("srcq_send")}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
